import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { lookupCard, CARD_INDEX, type CardIndexEntry } from "@/lib/pals-extras";
import { OFFICIAL_CARDS, officialCardImage } from "@/lib/tcg-cards";
import { RarityBadge, RarityLegend, rarityMeaning } from "@/components/RarityBadge";
import { ShareButton } from "@/components/ShareButton";
import { CardImage } from "@/components/CardImage";


const CANONICAL = "https://palstars.com/card-checker";
const SHARE_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/17714a28-4a57-4e59-8359-909140a1f8f4/id-preview-3bac5354--a3729fa2-e85b-4f5b-84b2-0934e880c580.lovable.app-1785103564262.png";

const TITLE = "Palworld TCG Card Checker — Number, Set & Rarity";
const DESC =
  "Check any Palworld Official Card Game card: paste a card number like EBP01-001 or a Pal name to confirm the set, rarity code, color and cost.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "How do I check if a Palworld card is real?",
    a: "Enter the card number printed in the bottom corner (for example EBP01-001) into the Pal Stars card checker. If the code returns a match, the set, rarity code, color and cost shown here should match the card in your hand — mismatched rarity or set codes are the fastest way to spot a fake or a proxy.",
  },
  {
    q: "Where is the card number on a Palworld TCG card?",
    a: "The card number sits in the lower band of the card face, formatted as set code, a dash, then the collector number — such as EBP01-014. Trial Deck cards use TD01 or TD02 prefixes instead of BP.",
  },
  {
    q: "What do the Palworld card rarity codes mean?",
    a: "C is Common, U is Uncommon, R is Rare, RR is Double Rare, SR is Super Rare, SP is a Special foil parallel, OSR is an Over Super Rare with an exclusive illustration, SSP is a Super Special Parallel with textured full-bleed art, and SSS is the single Ultra Secret Gold Card chase card. TD, TSR and TSP mark Trial Deck printings and PR marks promos. Parallels print the same collector number with the rarity appended, so EBP01-001 becomes EBP01-001OSR or EBP01-001SSP.",
  },
  {
    q: "How to check Palworld card rarity codes?",
    a: "Enter the printed card number or card name, then match the returned rarity badge to the lower-band code on the physical card. Parallel cards append the rarity to the collector number.",
  },
  {
    q: "Can I check several Palworld cards at once?",
    a: "Yes — search by Pal name or partial card code and the checker lists every matching card in the database so you can verify a whole play-set or binder page in one pass.",
  },
  {
    q: "Is the Pal Stars card checker official?",
    a: "No. Pal Stars is an unofficial fan database. Card data is compiled for identification and review purposes; always confirm high-value cards with the official Palworld Official Card Game resources or a professional grader.",
  },
];

export const Route = createFileRoute("/card-checker")({
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" && search.q ? { q: search.q } : {},
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Palworld TCG Card Checker — Pal Stars" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: SHARE_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld TCG Card Checker — Pal Stars" },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: SHARE_IMAGE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".aeo-answer"] },
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
            { "@type": "ListItem", position: 2, name: "TCG Cards", item: { "@id": "https://palstars.com/cards", name: "TCG Cards" } },
            { "@type": "ListItem", position: 3, name: "Card Checker", item: { "@id": CANONICAL, name: "Card Checker" } },
          ],
        }),
      },
    ],
  }),
  component: CardChecker,
});

function ResultRow({ c }: { c: CardIndexEntry }) {
  return (
    <div className="rounded-xl border bg-card/40 p-4">
      <div className="flex gap-4">
        {c.image && (
          <CardImage
            src={c.image}
            alt={`Palworld TCG card ${c.code} — ${c.name}`}
            width={120}
            height={168}
            className="shrink-0 rounded-lg"

          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary/15 px-2.5 py-1 font-mono text-xs font-bold text-primary">{c.code}</span>
            <span className="font-semibold">{c.name}</span>
            <span className="text-xs text-muted-foreground">{c.type} card</span>
            <RarityBadge code={c.rarity} />
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div><dt className="text-xs text-muted-foreground">Set</dt><dd className="font-medium">{c.set}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Rarity</dt><dd className="font-medium">{c.rarity}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Color</dt><dd className="font-medium">{c.color}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Cost</dt><dd className="font-medium">{c.cost}</dd></div>
          </dl>
          <p className="mt-2 text-sm text-muted-foreground">{c.detail}</p>
          <p className="mt-1 text-xs text-muted-foreground">{c.rarity}: {rarityMeaning(c.rarity)}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {c.palSlug && (
              <Link to="/pals/$slug" params={{ slug: c.palSlug }} className="rounded-full border px-3 py-1.5 hover:bg-card">
                View Pal stat sheet →
              </Link>
            )}
            <Link to="/cards" className="rounded-full border px-3 py-1.5 hover:bg-card">Open card database</Link>
            <Link to="/tcg-tools" className="rounded-full border px-3 py-1.5 hover:bg-card">Pull-rate calculator</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function CardChecker() {
  const { q = "" } = Route.useSearch();
  const navigate = useNavigate({ from: "/card-checker" });
  const [value, setValue] = useState(q);
  const results = useMemo(() => lookupCard(q), [q]);

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">Home</Link> /{" "}
        <Link to="/cards" className="hover:text-foreground">TCG Cards</Link> /{" "}
        <span className="text-foreground">Card Checker</span>
      </nav>

      <span className="text-[10px] uppercase tracking-widest text-primary">TCG Tool</span>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Palworld TCG Card Checker</h1>
      <p className="aeo-answer mt-3 text-muted-foreground">
        Paste a Palworld Official Card Game card number (for example <strong>EBP01-001</strong>) or a Pal name to
        instantly confirm the exact printed card number, set, rarity code, color and cost against {CARD_INDEX.length} indexed cards, including all {OFFICIAL_CARDS.length} printed Palworld OFFICIAL CARD GAME cards and their SR, SP, OSR, SSP, SSS, TSR and TSP parallels.
      </p>
      <div className="mt-3">
        <ShareButton title="Palworld TCG Card Checker" text={DESC} path="/card-checker" />
      </div>

      <RarityLegend codes={["C","U","R","RR","SR","SP","OSR","SSP","SSS","TD","TSR","TSP","PR"]} />

      <form
        className="mt-6 flex flex-col gap-2 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          void navigate({ search: { q: value.trim() } });
        }}
      >
        <label htmlFor="card-code" className="sr-only">Card number or Pal name</label>
        <input
          id="card-code"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="EBP01-001 or Lamball"
          aria-label="Card number or Pal name"
          className="flex-1 rounded-lg border bg-background/60 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
        />
        <button className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Check card
        </button>
      </form>

      <div className="mt-6 space-y-3">
        {q && results.length === 0 && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
            <strong>No match for “{q}”.</strong> That card number is not in the Pal Stars database. Double-check the set
            prefix (BP01, BP02, TD01, TD02) and the collector number, or search by the Pal name instead.
          </div>
        )}
        {results.map((c) => <ResultRow key={c.code + c.name} c={c} />)}
        {!q && (
          <div className="rounded-xl border bg-card/30 p-4 text-sm text-muted-foreground">
            Try a code like <button onClick={() => { setValue("EBP01-001"); void navigate({ search: { q: "EBP01-001" } }); }} className="text-primary hover:underline">EBP01-001</button>{" "}
            or a name like <button onClick={() => { setValue("Jetragon"); void navigate({ search: { q: "Jetragon" } }); }} className="text-primary hover:underline">Jetragon</button>.
          </div>
        )}
      </div>

      <section className="mt-12">
        <h2 className="text-xl font-bold">Palworld card checker FAQ</h2>
        <div className="mt-4 space-y-4">
          {FAQS.map((f) => (
            <div key={f.q} className="rounded-xl border bg-card/40 p-4">
              <h3 className="font-semibold">{f.q}</h3>
              <p className="aeo-answer mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-10 flex flex-wrap gap-2 text-xs">
        <Link to="/cards" className="rounded-full border px-3 py-1.5 hover:bg-card">Card Database</Link>
        <Link to="/tcg-tools" className="rounded-full border px-3 py-1.5 hover:bg-card">TCG Tools</Link>
        <Link to="/deck-builder" className="rounded-full border px-3 py-1.5 hover:bg-card">Deck Builder</Link>
        <Link to="/guides" className="rounded-full border px-3 py-1.5 hover:bg-card">All Guides</Link>
      </div>
    </div>
  );
}
